/**
 * Skool Over Training - Athlete Dashboard
 *
 * Redesigned to focus on "Today's Workout" as the primary view.
 * Now includes performance grade display (Poor -> Elite).
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useUser,
  listVideos,
  listChallenges,
  listTeams,
  getTeam,
  getAthleteProgress,
  type Organization,
  type Video,
  type Challenge,
  type Team,
  type TeamMember,
  type AthleteProgress,
  GRADE_CONFIG,
} from '@tss/shared';
import {
  Video as VideoIcon,
  Trophy,
  Target,
  ChevronRight,
  Users,
  X,
  User,
  Award,
  Calendar,
  TrendingUp,
  Sparkles,
} from 'lucide-react';
import { TodayWorkout } from '../components/TodayWorkout';
import { OnboardingChat } from '../components/OnboardingChat';
import { AnnouncementsFeed } from '../components/AnnouncementsFeed';
import { AIInsightsCard } from '../components/AIInsightsCard';
import { TeamMessageBoard } from '../components/TeamMessageBoard';
import { Button } from '@tss/shared/ui';
import { StatCard, Skeleton, Badge, PageHero, getInitialsFromName } from '../components/UI';

interface HomePageProps {
  org: Organization | null;
  proxyAthleteId?: string;
  proxyAthleteName?: string;
}

export function HomePage({ org, proxyAthleteId, proxyAthleteName }: HomePageProps) {
  const { displayName, userId, userProfile, loadUserProfile } = useUser();
  const navigate = useNavigate();

  // Use proxy athlete ID if in proxy mode, otherwise use logged-in user's ID
  const effectiveUserId = proxyAthleteId || userId;
  const effectiveDisplayName = proxyAthleteName || displayName;

  const [videos, setVideos] = useState<Video[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState<AthleteProgress | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check if profile is incomplete (show nudge if skipped) - only for non-proxy mode
  const isProfileIncomplete =
    !userProfile?.learning_style || !userProfile?.motivation_type || !userProfile?.position;
  const hasSkippedOnboarding = localStorage.getItem('onboardingSkipped') === 'true';
  const showProfileNudge = !proxyAthleteId && isProfileIncomplete && hasSkippedOnboarding;

  useEffect(() => {
    loadDashboardData();
  }, [org, effectiveUserId]);

  async function loadDashboardData() {
    setLoading(true);
    try {
      // Load videos
      // In proxy mode, filter to the specific athlete's videos
      const videosData = await listVideos(org?.org_id, false, proxyAthleteId);
      setVideos(videosData.videos || []);

      // Load challenges, teams, and progress if org exists
      if (org && effectiveUserId) {
        const [challengesData, teamsData, progressData] = await Promise.all([
          listChallenges(org.org_id).catch(() => ({ challenges: [] })),
          listTeams(org.org_id, proxyAthleteId).catch(() => ({ teams: [] })),
          getAthleteProgress(org.org_id, effectiveUserId).catch(() => null),
        ]);
        setChallenges(challengesData.challenges || []);
        setTeams(teamsData.teams || []);
        setProgress(progressData);
      }
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function openTeamModal(team: Team) {
    setSelectedTeam(team);
    setShowTeamModal(true);
    setLoadingTeam(true);
    try {
      if (org) {
        const data = await getTeam(org.org_id, team.team_id);
        setTeamMembers(data.members || []);
      }
    } catch (err) {
      console.error('Failed to load team members:', err);
    } finally {
      setLoadingTeam(false);
    }
  }

  const activeChallenges = challenges.filter((c) => c.status === 'ACTIVE');

  function handleStartOnboarding() {
    // Clear the skipped flag so onboarding shows
    localStorage.removeItem('onboardingSkipped');
    setShowOnboarding(true);
  }

  function handleOnboardingComplete() {
    setShowOnboarding(false);
    loadUserProfile();
  }

  function handleOnboardingSkip() {
    localStorage.setItem('onboardingSkipped', 'true');
    setShowOnboarding(false);
  }

  if (loading) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <Skeleton height={36} width="60%" style={{ marginBottom: 8 }} />
        <Skeleton height={20} width="40%" style={{ marginBottom: 32 }} />
        <Skeleton.Stats count={4} />
        <div style={{ marginTop: 24 }}>
          <Skeleton.Card count={2} />
        </div>
      </div>
    );
  }

  // Get grade info from progress
  const monthlyGrade = progress?.monthly?.grade || 'DEVELOPING';
  const gradeInfo = GRADE_CONFIG[monthlyGrade] || GRADE_CONFIG.DEVELOPING;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Onboarding chat modal */}
      {showOnboarding && (
        <OnboardingChat
          userName={displayName}
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      {/* Page hero — identity-strong block (SKOOL-33) */}
      <div style={{ marginBottom: 24 }}>
        <PageHero
          eyebrow={proxyAthleteId ? 'PROXY VIEW' : 'WELCOME BACK'}
          title={`Hey, ${effectiveDisplayName || 'Athlete'}!`}
          meta={
            proxyAthleteId
              ? [org?.name?.toUpperCase() || null]
              : [
                  userProfile?.position?.toUpperCase() || null,
                  userProfile?.graduation_year ? `CLASS OF ${userProfile.graduation_year}` : null,
                  org?.name?.toUpperCase() || null,
                ]
          }
          avatarSrc={!proxyAthleteId ? (userProfile?.profile_picture_url ?? undefined) : undefined}
          avatarInitials={getInitialsFromName(effectiveDisplayName)}
        />
      </div>

      {/* Profile completion nudge */}
      {showProfileNudge && (
        <button
          onClick={handleStartOnboarding}
          className="card mb-6"
          style={{
            width: '100%',
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background:
              'linear-gradient(135deg, rgba(var(--purple-rgb), 0.08) 0%, rgba(var(--purple-rgb), 0.04) 100%)',
            border: '1px solid rgba(var(--purple-rgb), 0.15)',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'rgba(var(--purple-rgb), 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Sparkles size={18} style={{ color: 'var(--purple-600)' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontWeight: '500', color: 'var(--gray-900)', fontSize: '14px' }}>
              Finish setting up your profile
            </p>
            <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '2px' }}>
              Help your coaches personalize your training
            </p>
          </div>
          <ChevronRight size={18} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
        </button>
      )}

      {/* Announcements Feed */}
      <section className="page-section">
        <header className="page-section-header">
          <p className="page-section-label">UPDATES</p>
          <h2 className="page-section-title">Announcements</h2>
          <div className="page-section-rule" aria-hidden="true" />
        </header>
        <AnnouncementsFeed trigger="login" maxItems={3} />
      </section>

      {/* Monthly Performance Grade - Prominent Display */}
      {progress && (
        <div
          className="card mb-6 animate-fade-in"
          style={{
            background: `linear-gradient(135deg, ${gradeInfo.color}15 0%, ${gradeInfo.color}05 100%)`,
            border: `2px solid ${gradeInfo.color}30`,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flex: '1 1 auto',
                minWidth: 0,
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  minWidth: '48px',
                  borderRadius: '50%',
                  background: `${gradeInfo.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                }}
              >
                {gradeInfo.emoji}
              </div>
              <div style={{ minWidth: 0 }}>
                <p
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: 'var(--gray-500)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '2px',
                  }}
                >
                  This Month's Grade
                </p>
                <h2
                  style={{ fontSize: '22px', fontWeight: '800', color: gradeInfo.color, margin: 0 }}
                >
                  {gradeInfo.label}
                </h2>
                <p
                  style={{
                    fontSize: '12px',
                    color: 'var(--gray-600)',
                    marginTop: '2px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {gradeInfo.description}
                </p>
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <p
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: gradeInfo.color,
                  lineHeight: 1,
                }}
              >
                {progress.monthly.completion_percent}%
              </p>
              <p style={{ fontSize: '11px', color: 'var(--gray-500)', marginTop: '4px' }}>
                {progress.monthly.drills_completed}/{progress.monthly.drills_assigned} drills
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div style={{ marginTop: '16px' }}>
            <div
              style={{
                height: '6px',
                background: 'rgba(0,0,0,0.1)',
                borderRadius: '3px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(progress.monthly.completion_percent, 100)}%`,
                  background: gradeInfo.color,
                  borderRadius: '3px',
                  transition: 'width 0.5s ease',
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '6px',
                fontSize: '10px',
                color: 'var(--gray-400)',
              }}
            >
              <span>Poor</span>
              <span>Developing</span>
              <span>Good</span>
              <span>Great</span>
              <span>Elite</span>
            </div>
          </div>

          {/* Quick stats row */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              marginTop: '14px',
              paddingTop: '14px',
              borderTop: `1px solid ${gradeInfo.color}20`,
            }}
          >
            <div style={{ minWidth: '60px' }}>
              <p style={{ fontSize: '16px', fontWeight: '700', color: 'var(--gray-900)' }}>
                {progress.monthly.active_days}
              </p>
              <p style={{ fontSize: '10px', color: 'var(--gray-500)' }}>Active Days</p>
            </div>
            <div style={{ minWidth: '50px' }}>
              <p style={{ fontSize: '16px', fontWeight: '700', color: 'var(--gray-900)' }}>
                {progress.monthly.videos_uploaded}
              </p>
              <p style={{ fontSize: '10px', color: 'var(--gray-500)' }}>Videos</p>
            </div>
            <div style={{ minWidth: '60px' }}>
              <p style={{ fontSize: '16px', fontWeight: '700', color: '#f59e0b' }}>
                🔥 {progress.monthly.streak_days}
              </p>
              <p style={{ fontSize: '10px', color: 'var(--gray-500)' }}>Day Streak</p>
            </div>
            {(progress.monthly.excellent_flags || 0) > 0 && (
              <div style={{ minWidth: '60px' }}>
                <p style={{ fontSize: '16px', fontWeight: '700', color: 'var(--purple-500)' }}>
                  ⭐ {progress.monthly.excellent_flags}
                </p>
                <p style={{ fontSize: '10px', color: 'var(--gray-500)' }}>Coach Stars</p>
              </div>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
              <button
                onClick={() => navigate('/progress')}
                className="btn-ghost"
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  color: gradeInfo.color,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                View Full Progress
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Coach Insights */}
      <section className="page-section">
        <header className="page-section-header">
          <p className="page-section-label">AI INSIGHTS</p>
          <h2 className="page-section-title">Coaching insights for you</h2>
          <div className="page-section-rule" aria-hidden="true" />
        </header>
        <AIInsightsCard onViewFullProgress={() => navigate('/progress')} />
      </section>

      {/* Today's Workout - Primary Focus */}
      <section className="page-section">
        <header className="page-section-header">
          <p className="page-section-label">TODAY</p>
          <h2 className="page-section-title">Your training</h2>
          <div className="page-section-rule" aria-hidden="true" />
        </header>
        <TodayWorkout org={org} proxyAthleteId={proxyAthleteId} />
      </section>

      {/* Quick stats */}
      <div
        className="grid gap-3 mb-6"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}
      >
        <StatCard
          icon={VideoIcon}
          label="My Videos"
          value={videos.length}
          color="var(--color-success)"
          onClick={() => navigate('/videos')}
          compact
        />
        <StatCard
          icon={TrendingUp}
          label="My Progress"
          value={progress ? `${progress.monthly.completion_percent}%` : '—'}
          color={progress ? gradeInfo.color : 'var(--purple-600)'}
          onClick={() => navigate('/progress')}
          compact
        />
        <StatCard
          icon={Trophy}
          label="Challenges"
          value={activeChallenges.length}
          color="#f59e0b"
          onClick={() => navigate('/challenges')}
          compact
        />
        <StatCard
          icon={Users}
          label="My Teams"
          value={teams.length}
          color="var(--purple-500)"
          onClick={teams.length > 0 ? () => openTeamModal(teams[0]) : undefined}
          compact
        />
      </div>

      {/* Secondary content */}
      {videos.length > 0 && (
        <div className="card mb-6">
          <h3
            className="flex items-center gap-2"
            style={{ marginBottom: '16px', fontSize: '16px' }}
          >
            <div
              className="icon-container icon-container-sm"
              style={{ background: 'rgba(var(--gold-rgb), 0.1)' }}
            >
              <VideoIcon size={16} style={{ color: 'var(--color-success)' }} />
            </div>
            Recent Videos
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {videos.slice(0, 3).map((video) => (
              <VideoItem key={video.video_id} video={video} />
            ))}
          </div>
          <Button
            variant="ghost"
            fullWidth
            onClick={() => navigate('/videos')}
            iconRight={<ChevronRight size={16} />}
            style={{ marginTop: '16px' }}
          >
            View All
          </Button>
        </div>
      )}

      {/* My Teams */}
      {teams.length > 0 && (
        <div className="card mb-6">
          <h3
            className="flex items-center gap-2"
            style={{ marginBottom: '16px', fontSize: '16px' }}
          >
            <div
              className="icon-container icon-container-sm"
              style={{ background: 'rgba(var(--purple-rgb), 0.1)' }}
            >
              <Users size={16} style={{ color: 'var(--purple-500)' }} />
            </div>
            My Teams
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {teams.slice(0, 3).map((team) => (
              <TeamItem key={team.team_id} team={team} onClick={() => openTeamModal(team)} />
            ))}
          </div>
        </div>
      )}

      {/* Active Challenges */}
      {activeChallenges.length > 0 && (
        <div className="card">
          <h3
            className="flex items-center gap-2"
            style={{ marginBottom: '16px', fontSize: '16px' }}
          >
            <div
              className="icon-container icon-container-sm"
              style={{ background: 'rgba(245, 158, 11, 0.1)' }}
            >
              <Trophy size={16} style={{ color: '#f59e0b' }} />
            </div>
            Active Challenges
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {activeChallenges.slice(0, 3).map((challenge) => (
              <ChallengeItem key={challenge.challenge_id} challenge={challenge} />
            ))}
          </div>
          <Button
            variant="ghost"
            fullWidth
            onClick={() => navigate('/challenges')}
            iconRight={<ChevronRight size={16} />}
            style={{ marginTop: '16px' }}
          >
            View All
          </Button>
        </div>
      )}

      {/* Team Modal */}
      {showTeamModal && selectedTeam && org && (
        <TeamModal
          team={selectedTeam}
          members={teamMembers}
          loading={loadingTeam}
          onClose={() => {
            setShowTeamModal(false);
            setSelectedTeam(null);
            setTeamMembers([]);
          }}
          orgId={org.org_id}
          proxyAthleteId={proxyAthleteId}
        />
      )}
    </div>
  );
}

function VideoItem({ video }: { video: Video }) {
  const getStatusVariant = (
    status: string
  ): { label: string; variant: 'success' | 'primary' | 'accent' | 'default' } => {
    switch (status) {
      case 'READY':
        return { label: 'Ready', variant: 'success' };
      case 'PROCESSING':
        return { label: 'Processing', variant: 'primary' };
      case 'REVIEWED':
        return { label: 'Reviewed', variant: 'accent' };
      default:
        return { label: status, variant: 'default' };
    }
  };
  const { label, variant } = getStatusVariant(video.status);

  return (
    <div className="card card-interactive" style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: 'var(--radius-sm)',
            background: 'linear-gradient(135deg, var(--purple-600) 0%, var(--purple-500) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <VideoIcon size={18} style={{ color: 'white' }} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p
            className="truncate"
            style={{ fontWeight: '500', color: 'var(--gray-900)', fontSize: '14px' }}
          >
            {video.title}
          </p>
          <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
            {video.status === 'READY' ? 'Ready for review' : 'Training video'}
          </p>
        </div>
        <Badge variant={variant} size="sm" pill>
          {label}
        </Badge>
      </div>
    </div>
  );
}

function ChallengeItem({ challenge }: { challenge: Challenge }) {
  return (
    <div className="card card-interactive" style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: 'var(--radius-sm)',
            background: 'linear-gradient(135deg, #f59e0b 0%, var(--gold-600) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Trophy size={18} style={{ color: 'white' }} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p
            className="truncate"
            style={{ fontWeight: '500', color: 'var(--gray-900)', fontSize: '14px' }}
          >
            {challenge.title}
          </p>
          <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
            {challenge.participant_ids.length} participants
          </p>
        </div>
        <Badge variant="success" size="sm" pill>
          Active
        </Badge>
      </div>
    </div>
  );
}

function TeamItem({ team, onClick }: { team: Team; onClick: () => void }) {
  return (
    <div className="card card-interactive" style={{ padding: '14px 16px' }} onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: team.color || 'linear-gradient(135deg, var(--purple-500) 0%, #6366F1 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Users size={18} style={{ color: 'white' }} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <p
            className="truncate"
            style={{ fontWeight: '500', color: 'var(--gray-900)', fontSize: '14px' }}
          >
            {team.name}
          </p>
          <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
            {team.member_count || 0} members {team.coach_name && `• ${team.coach_name}`}
          </p>
        </div>
        <ChevronRight size={18} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
      </div>
    </div>
  );
}

function TeamModal({
  team,
  members,
  loading,
  onClose,
  orgId,
  proxyAthleteId,
}: {
  team: Team;
  members: TeamMember[];
  loading: boolean;
  onClose: () => void;
  orgId: string;
  proxyAthleteId?: string;
}) {
  const [activeTab, setActiveTab] = useState<'members' | 'messages'>('messages');

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
    >
      <div
        className="card animate-fade-in"
        style={{
          width: '100%',
          maxWidth: 'min(500px, calc(100vw - 32px))',
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--gray-100)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background:
                  team.color || 'linear-gradient(135deg, var(--purple-500) 0%, #6366F1 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Users size={24} style={{ color: 'white' }} />
            </div>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--gray-900)' }}>
                {team.name}
              </h2>
              {team.description && (
                <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '2px' }}>
                  {team.description}
                </p>
              )}
            </div>
          </div>
          <Button variant="ghost" onClick={onClose} aria-label="Close">
            <X size={20} />
          </Button>
        </div>

        {/* Team Info */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--gray-100)' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {team.coach_name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Award size={14} style={{ color: 'var(--purple-600)' }} />
                <span style={{ fontSize: '13px', color: 'var(--gray-600)' }}>
                  Coach: {team.coach_name}
                </span>
              </div>
            )}
            {team.skill_level && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Target size={14} style={{ color: 'var(--color-success)' }} />
                <span
                  style={{
                    fontSize: '13px',
                    color: 'var(--gray-600)',
                    textTransform: 'capitalize',
                  }}
                >
                  {team.skill_level}
                </span>
              </div>
            )}
            {team.age_range && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Calendar size={14} style={{ color: '#f59e0b' }} />
                <span style={{ fontSize: '13px', color: 'var(--gray-600)' }}>
                  Ages: {team.age_range}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--gray-100)' }}>
          <button
            onClick={() => setActiveTab('messages')}
            style={{
              flex: 1,
              padding: '12px',
              fontSize: '14px',
              fontWeight: activeTab === 'messages' ? '600' : '400',
              color: activeTab === 'messages' ? 'var(--purple-600)' : 'var(--gray-500)',
              background: 'transparent',
              border: 'none',
              borderBottom:
                activeTab === 'messages' ? '2px solid var(--purple-600)' : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            Messages
          </button>
          <button
            onClick={() => setActiveTab('members')}
            style={{
              flex: 1,
              padding: '12px',
              fontSize: '14px',
              fontWeight: activeTab === 'members' ? '600' : '400',
              color: activeTab === 'members' ? 'var(--purple-600)' : 'var(--gray-500)',
              background: 'transparent',
              border: 'none',
              borderBottom:
                activeTab === 'members' ? '2px solid var(--purple-600)' : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            Members ({members.length})
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
          {activeTab === 'messages' ? (
            <TeamMessageBoard orgId={orgId} teamId={team.team_id} proxyAthleteId={proxyAthleteId} />
          ) : (
            <>
              {loading ? (
                <div className="flex-center" style={{ padding: '40px' }}>
                  <div
                    className="animate-spin"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      border: '3px solid var(--gray-200)',
                      borderTopColor: 'var(--purple-600)',
                    }}
                  />
                </div>
              ) : members.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--gray-500)' }}>
                  <Users size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
                  <p>No team members yet</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {members.map((member) => (
                    <div
                      key={member.user_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        background: 'var(--gray-50)',
                        borderRadius: 'var(--radius-md)',
                      }}
                    >
                      {member.profile_picture_url ? (
                        <img
                          src={member.profile_picture_url}
                          alt={`${member.given_name} ${member.family_name}`}
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            objectFit: 'cover',
                            objectPosition: 'center 20%',
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            background:
                              'linear-gradient(135deg, var(--purple-600) 0%, var(--purple-500) 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <User size={20} style={{ color: 'white' }} />
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p
                          style={{ fontWeight: '500', color: 'var(--gray-900)', fontSize: '14px' }}
                        >
                          {member.given_name} {member.family_name}
                        </p>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            flexWrap: 'wrap',
                            marginTop: '2px',
                          }}
                        >
                          {member.position && (
                            <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                              {member.position}
                            </span>
                          )}
                          {member.jersey_number && (
                            <Badge variant="primary" size="xs" uppercase={false}>
                              #{member.jersey_number}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
